import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import Table from '@risksmart-app/components/src/table';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmModal from 'src/components/confirm-modal/ConfirmModal';
import TabHeader from 'src/components/tab-header';
import { useGetThirdPartyContacts } from 'src/hooks/queries/third-party/useGetThirdPartyContacts';

import {
  useRestoreThirdPartyContactAccess,
  useRevokeThirdPartyContactAccess,
} from '@/hooks/mutations/third-party-contact';

import { useGetContactsTableProps } from './config';
import { AddContactModal } from './modals/AddContactModal';
import RevokeContactsModal from './modals/RevokeContactsModal';
import { ViewContactModal } from './modals/ViewContactModal';
import type {
  ThirdPartyContactFields,
  ThirdPartyContactWithStatus,
} from './types';
import { useContactsWithStatus } from './useContactsWithStatus';
import { useRestoreContacts } from './useRestoreContacts';
import { useRevokeContacts } from './useRevokeContacts';

const ContactsTab = () => {
  const thirdPartyId = useGetGuidParam('id');
  const { t } = useTranslation(['common'], {
    keyPrefix: 'third_party.contacts',
  });
  const { addNotification } = useNotifications();

  // State for modals
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [selectedContact, setSelectedContact] =
    useState<ThirdPartyContactWithStatus | null>(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);

  // State for multiselect
  const [selectedContacts, setSelectedContacts] = useState<
    ThirdPartyContactWithStatus[]
  >([]);

  // Revoke mutation
  const { revokeThirdPartyContactAccess, loading: isRevoking } =
    useRevokeThirdPartyContactAccess();
  const {
    mutateAsync: restoreThirdPartyContactAccess,
    isPending: isRestoring,
  } = useRestoreThirdPartyContactAccess();

  // Query contacts using wrapper hook (handles tRPC/GraphQL switching)
  const {
    data: contactsResult,
    loading: contactsLoading,
    refetch: refetchContacts,
  } = useGetThirdPartyContacts({
    queryArgs: { thirdPartyId, includeRevoked: true },
  });

  const contactsData =
    (contactsResult?.third_party_contact as ThirdPartyContactFields[]) ?? [];

  const existingEmails = useMemo(
    () => contactsData.map((c) => c.Email.toLowerCase()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contactsResult]
  );

  // Map contacts with status based on user's LastSeen
  const contacts = useContactsWithStatus(contactsData);

  const handleViewContact = useCallback(
    (contact: ThirdPartyContactWithStatus) => {
      setSelectedContact(contact);
    },
    []
  );

  const tableProps = useGetContactsTableProps(contacts, handleViewContact);
  const loading = contactsLoading;

  const handleAddSuccess = useCallback(() => {
    setIsAddModalVisible(false);
    refetchContacts();
  }, [refetchContacts]);

  const { revocableContacts, handleRevokeAccess } = useRevokeContacts({
    thirdPartyId,
    selectedContacts,
    revokeContacts: revokeThirdPartyContactAccess,
    addNotification,
    refetchContacts,
    onSuccess: () => setSelectedContacts([]),
  });
  const { restorableContacts, handleRestoreAccess } = useRestoreContacts({
    thirdPartyId,
    selectedContacts,
    restoreContacts: restoreThirdPartyContactAccess,
    addNotification,
    refetchContacts,
    onSuccess: () => {
      setSelectedContacts([]);
      setShowRestoreConfirm(false);
    },
  });

  return (
    <div>
      <Table
        {...tableProps}
        trackBy={'Id'}
        header={
          <TabHeader
            actions={
              <SpaceBetween direction={'horizontal'} size={'xs'}>
                <Button
                  formAction={'none'}
                  variant={'normal'}
                  disabled={!restorableContacts.length || isRestoring}
                  loading={isRestoring}
                  onClick={() => setShowRestoreConfirm(true)}
                >
                  {t('restore_access_button')}
                </Button>
                <Button
                  formAction={'none'}
                  variant={'normal'}
                  disabled={!revocableContacts.length || isRevoking}
                  onClick={() => setShowRevokeConfirm(true)}
                >
                  {t('revoke_access_button')}
                </Button>
                <Button
                  variant={'primary'}
                  onClick={() => setIsAddModalVisible(true)}
                >
                  {t('create_new_button')}
                </Button>
              </SpaceBetween>
            }
          >
            {t('register_title')}
          </TabHeader>
        }
        loading={loading}
        variant={'embedded'}
        selectionType={'multi'}
        selectedItems={selectedContacts}
        onSelectionChange={({ detail }) => {
          setSelectedContacts(detail.selectedItems);
        }}
      />
      <AddContactModal
        thirdPartyId={thirdPartyId}
        existingEmails={existingEmails}
        isVisible={isAddModalVisible}
        onDismiss={() => setIsAddModalVisible(false)}
        onSuccess={handleAddSuccess}
      />
      {selectedContact && (
        <ViewContactModal
          contact={selectedContact}
          onDismiss={() => setSelectedContact(null)}
        />
      )}
      <RevokeContactsModal
        isVisible={showRevokeConfirm}
        loading={isRevoking}
        onRevoke={handleRevokeAccess}
        onDismiss={() => setShowRevokeConfirm(false)}
      />
      <ConfirmModal
        isVisible={showRestoreConfirm}
        loading={isRestoring}
        onConfirm={handleRestoreAccess}
        onDismiss={() => setShowRestoreConfirm(false)}
        header={t('restore_access_modal_title')}
        confirmLabel={t('restore_access_modal_button')}
      >
        {t('restore_access_confirm_multiple')}
      </ConfirmModal>
    </div>
  );
};

export default ContactsTab;
